
import { useState, useEffect, useCallback } from 'react';
import { activityApi } from '@/services/api/activityApi';
import { CallLog, CreateCallLogRequest, UpdateCallLogRequest } from '@/types/callLog';

interface UseCallLogsOptions {
  shouldFetch?: boolean;
}

export const useCallLogs = (userId?: number, options: UseCallLogsOptions = {}) => {
  const { shouldFetch = true } = options;
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCallLogs = useCallback(async () => {
    console.log('useCallLogs: fetchCallLogs called with userId:', userId, 'shouldFetch:', shouldFetch);
    if (!userId || !shouldFetch) {
      console.log('useCallLogs: No userId provided or fetch disabled, skipping fetch');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('useCallLogs: Calling activityApi.getUserActivities with userId:', userId);
      const activityResponse = await activityApi.getUserActivities(userId, undefined, undefined, 50);
      
      // Transform call activities to CallLog format
      const logs: CallLog[] = activityResponse.activities
        .filter(activity => activity.activity_type === 'call')
        .map(activity => ({
          id: activity.id,
          user_id: userId,
          phone_number: extractPhoneFromDescription(activity.activity_description || ''),
          call_status: (activity.activity_status as any) || 'unknown',
          call_start_time: activity.activity_date,
          call_duration: activity.duration_minutes || 0,
          call_sid: '', // Not available in activity API
          call_price: null, // Not available in activity API
          call_price_unit: null, // Not available in activity API
          ai_summary: activity.activity_description || '',
          created_at: activity.activity_date,
          updated_at: activity.updated_at
        }));
      
      console.log('useCallLogs: Received logs:', logs);
      setCallLogs(logs);
    } catch (err) {
      console.error('useCallLogs: Error fetching call logs:', err);
      setError('Failed to fetch call logs');
    } finally {
      setLoading(false);
    }
  }, [userId, shouldFetch]);

  const saveCallLog = useCallback(async (callLogData: CreateCallLogRequest): Promise<CallLog | null> => {
    try {
      setError(null);
      // Note: This function now logs activities instead of call logs
      // For now, returning null as the activity API doesn't return CallLog format
      console.warn('saveCallLog: This function should create activities instead of call logs');
      return null;
    } catch (err: any) {
      setError(err.message || 'Failed to save call log');
      console.error('Error saving call log:', err);
      throw err; // Re-throw to be caught by the caller
    }
  }, []);

  const updateCallLog = useCallback(async (logId: number, updates: UpdateCallLogRequest): Promise<CallLog | null> => {
    try {
      setError(null);
      // Note: This function should update activities instead of call logs
      console.warn('updateCallLog: This function should update activities instead of call logs');
      return null;
    } catch (err: any) {
      setError(err.message || 'Failed to update call log');
      console.error(`Error updating call log ${logId}:`, err);
      throw err; // Re-throw
    }
  }, []);

  useEffect(() => {
    console.log('useCallLogs: useEffect triggered, userId:', userId, 'shouldFetch:', shouldFetch);
    fetchCallLogs();
  }, [fetchCallLogs]);

  return {
    callLogs,
    loading,
    error,
    refetch: fetchCallLogs,
    saveCallLog,
    updateCallLog // Expose the new function
  };
};

// Helper function to extract phone number from activity description
const extractPhoneFromDescription = (description: string): string => {
  // Look for phone number patterns in the description
  const phoneRegex = /\+[\d\s-()]+/;
  const match = description.match(phoneRegex);
  return match ? match[0] : '';
};
